/**
 * DevUserSwitcher — header control to ACT AS another user while testing (Spec 255).
 *
 * Non-production only (same gate as DevRoleSwitcher / EnvironmentBadge). Picks a real
 * dbo.users identity; the choice is sent as X-Dev-Assume-User-Id on every request and the
 * backend (non-prod only) overrides req.user with it — so ack/resolve/etc. are attributed to
 * the chosen person. Lets one tester exercise the multi-user alert flow (acknowledge as A,
 * resolve as B) from a single session. "Act as: Me" clears the override.
 *
 * 🛑 SPEC 241 — CROSS-TENANT ACTING. The roster is now built from EVERY registered tenant, not
 *    just the caller's, so a tester can act as a tenant-2 ([T2]) user and eyeball tenant
 *    separation through the real UI. This is the whole point: the roster is data-driven off
 *    `/api/dev/tenants`, and picking a user in another tenant sets `dev_assume_tenant_id` as
 *    well as `dev_assume_user_id`. Without that tenant cookie/header the backend's
 *    tenant-scoped `userRepository.getById` runs under the CALLER's tenant, finds nobody, and
 *    silently serves the caller's own data under the other name — a false green. Both are set
 *    together, and both are cleared by "Act as: Me". `apiClient` already forwards the matching
 *    `X-Dev-Assume-Tenant-Id` header for the cross-origin (local Vite → :3001) data path.
 */
import React from 'react';
import { Select } from '@/components/ui';
import { AppTooltip } from '@/components/ui/AppTooltip';
import { apiClient } from '../../services/apiClient';
import {
  setDevAssumeUserId, clearDevAssumeUserId,
  setDevAssumeTenantId, clearDevAssumeTenantId,
} from '../../auth/devUserStore';
import { useAuth } from '../../auth/hooks/useAuth';
import { useEnvironment } from '../../modules/backup/hooks/useEnvironment';

interface DevUser { id: string; name: string; role: string; email: string }
interface DevTenant { id: string; name: string; status: string }
interface UserRow extends DevUser { tenantId: string; tenantName: string; tenantStatus: string }

/**
 * The `dev_assume_user_id` COOKIE is the backend's source of truth (it rides every request).
 * Read it — not sessionStorage — so the dropdown always reflects who you're actually acting as.
 * (sessionStorage is per-tab and empties on a new tab while the cookie persists, which stranded
 * the UI on "Me" while the API still saw the impersonated user → Admin screens 403'd.)
 */
function readAssumeCookie(): string {
  const m = typeof document !== 'undefined'
    ? document.cookie.match(/(?:^|;\s*)dev_assume_user_id=([^;]*)/)
    : null;
  return m ? decodeURIComponent(m[1]) : '';
}

export const DevUserSwitcher: React.FC = () => {
  const { user } = useAuth();
  const { environmentInfo } = useEnvironment();
  const [users, setUsers] = React.useState<UserRow[]>([]);
  const [busy, setBusy] = React.useState(false);

  const nonProd = Boolean(environmentInfo && environmentInfo.environment !== 'production' && user);

  React.useEffect(() => {
    if (!nonProd) return;
    let cancelled = false;
    (async () => {
      try {
        // Data-driven off the registry: every tenant, not a hardcoded id, so a future tenant 3
        // appears here with no code change. Each tenant's roster comes from the tenant-bound
        // `?tenant=` read (the own tenant included — it binds to itself, same result).
        const t = await apiClient.get<{ tenants: DevTenant[] }>('/api/dev/tenants');
        const tenants = t.data.tenants ?? [];
        const perTenant = await Promise.all(
          tenants.map(async (tn) => {
            try {
              const r = await apiClient.get<{ users: DevUser[] }>(
                `/api/dev/users?tenant=${encodeURIComponent(tn.id)}`,
              );
              return (r.data.users ?? []).map((u) => ({
                ...u, tenantId: tn.id, tenantName: tn.name, tenantStatus: tn.status,
              }));
            } catch {
              return [] as UserRow[];
            }
          }),
        );
        if (!cancelled) setUsers(perTenant.flat());
      } catch {
        // Fallback: caller's tenant only (e.g. /api/dev/tenants unavailable on an older backend).
        try {
          const r = await apiClient.get<{ users: DevUser[] }>('/api/dev/users');
          if (!cancelled) {
            setUsers((r.data.users ?? []).map((u) => ({
              ...u, tenantId: '', tenantName: '', tenantStatus: '',
            })));
          }
        } catch {
          if (!cancelled) setUsers([]);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [nonProd]);

  if (!nonProd) return null;

  // Reflect the actual cookie (backend source of truth), so "Me" can never be shown while a
  // stale impersonation cookie is still in effect.
  const assumed = readAssumeCookie();

  const onChange = (id: string): void => {
    if (busy) return;
    setBusy(true);
    // A cookie (not a header) is the source of truth for same-origin requests: it rides EVERY
    // one automatically — including the /api/auth/session fetch and any HTTP client. The store
    // (sessionStorage) additionally drives the X-Dev-Assume-* HEADERS apiClient injects, which
    // is what carries the assume across the cross-origin (Vite :3000 → :3001) data path. Set
    // BOTH, for both halves, exactly as the user cookie already does.
    if (id) {
      const row = users.find((u) => u.id === id);
      document.cookie = `dev_assume_user_id=${encodeURIComponent(id)}; path=/; SameSite=Lax`;
      setDevAssumeUserId(id);
      // 🛑 Set the tenant alongside the user. Without it the backend's tenant-scoped lookup runs
      //    under the caller's tenant, finds nobody, and serves the caller's own data under the
      //    other name — a false green. tenantId is '' only on the older-backend fallback path,
      //    where every user is the caller's tenant anyway, so clearing it is correct there.
      if (row?.tenantId) {
        document.cookie = `dev_assume_tenant_id=${encodeURIComponent(row.tenantId)}; path=/; SameSite=Lax`;
        setDevAssumeTenantId(row.tenantId);
      } else {
        document.cookie = 'dev_assume_tenant_id=; path=/; Max-Age=0; SameSite=Lax';
        clearDevAssumeTenantId();
      }
    } else {
      document.cookie = 'dev_assume_user_id=; path=/; Max-Age=0; SameSite=Lax';
      clearDevAssumeUserId();
      document.cookie = 'dev_assume_tenant_id=; path=/; Max-Age=0; SameSite=Lax';
      clearDevAssumeTenantId();
    }
    // Reload so AuthProvider + every query re-derive as the impersonated identity.
    window.location.reload();
  };

  return (
    <AppTooltip title="Dev only: act as another user — including a [T2] user in another tenant — to test multi-user flows and tenant separation through the real UI. Picking a cross-tenant user binds that tenant too. Choose 'Me' to reset.">
      <div className="hidden md:block">
        <Select
          aria-label="Act as user (dev only)"
          selectSize="sm"
          value={assumed}
          disabled={busy || users.length === 0}
          onChange={(e) => onChange(e.target.value)}
          options={[
            { value: '', label: 'Act as: Me' },
            ...users.map((u) => {
              const suffix = u.tenantName
                ? ` · ${u.tenantName}${u.tenantStatus && u.tenantStatus !== 'Active' ? ` [${u.tenantStatus}]` : ''}`
                : '';
              return { value: u.id, label: `Act as: ${u.name} (${u.role})${suffix}` };
            }),
          ]}
        />
      </div>
    </AppTooltip>
  );
};

export default DevUserSwitcher;
